// hooks/useProductQueue.js
// Background save queue with proper thunk dispatch and FormData handling
// IMPROVED: Better error handling, edit detection, duplicate prevention

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import toast from "react-hot-toast";

// ✅ UPDATE THIS IMPORT based on your project structure
import { createProductThunk } from "../../../../store/thunks/adminProductThunks";

const MAX_CONCURRENT = 2;
const RETRY_DELAY = 3000;
const MAX_RETRIES = 3;

export const useProductQueue = (dispatch, onStatusChange) => {
  const [queue, setQueue] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const processingRef = useRef(new Set());
  const retryCountRef = useRef({});
  // Track data version to detect edits
  const dataVersionRef = useRef({});

  // Check if item is already in queue (pending or processing)
  const isInQueue = useCallback(
    (itemId) => {
      return queue.some(
        (item) =>
          item.id === itemId &&
          (item.status === "pending" || item.status === "processing")
      );
    },
    [queue]
  );

  // Get item from queue
  const getQueueItem = useCallback(
    (itemId) => {
      return queue.find((item) => item.id === itemId);
    },
    [queue]
  );

  // Add item to queue with duplicate prevention
  const addToQueue = useCallback(
    (item) => {
      // Check for duplicate - don't add if already pending/processing
      if (isInQueue(item.id)) {
        toast.error(`"${item.name}" is already in the save queue`);
        return null;
      }

      // If item was previously failed/completed, remove old entry first
      setQueue((prev) => prev.filter((q) => q.id !== item.id));

      const queueItem = {
        id: item.id,
        data: item.data,
        name: item.name || item.data.name || "Untitled",
        status: "pending",
        addedAt: Date.now(),
        completedAt: null,
        error: null,
        dataVersion: Date.now(), // Track when data was added
      };

      // Store data version
      dataVersionRef.current[item.id] = queueItem.dataVersion;

      setQueue((prev) => [...prev, queueItem]);
      retryCountRef.current[item.id] = 0;

      return queueItem;
    },
    [isInQueue]
  );

  // Update item data in queue (for re-save after edit)
  const updateQueueItemData = useCallback((itemId, newData, newName) => {
    const newVersion = Date.now();
    dataVersionRef.current[itemId] = newVersion;

    setQueue((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              data: newData,
              name: newName || newData.name || item.name,
              dataVersion: newVersion,
              // Reset error if data was updated
              error: null,
            }
          : item
      )
    );
  }, []);

  // Remove item from queue
  const removeFromQueue = useCallback((itemId) => {
    setQueue((prev) => prev.filter((item) => item.id !== itemId));
    processingRef.current.delete(itemId);
    delete retryCountRef.current[itemId];
    delete dataVersionRef.current[itemId];
  }, []);

  // Update item status
  const updateItemStatus = useCallback(
    (itemId, status, error = null, savedProductId = null) => {
      setQueue((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? {
                ...item,
                status,
                error,
                savedProductId,
                completedAt:
                  status === "completed" || status === "failed"
                    ? Date.now()
                    : null,
              }
            : item
        )
      );

      if (onStatusChange) {
        onStatusChange(itemId, status, error, savedProductId);
      }
    },
    [onStatusChange]
  );

  // ============= BUILD FORMDATA - ALIGNED WITH PRODUCTFORM =============
  const buildFormData = useCallback((productData) => {
    const submitData = new FormData();

    // ===== BASIC FIELDS =====
    submitData.append("name", productData.name?.trim() || "");
    submitData.append("description", productData.description || "");
    submitData.append("type", productData.type || "GOLD");
    submitData.append("subType", productData.subType || "");
    submitData.append("status", productData.status || "DRAFT");
    submitData.append("gender", productData.gender || "UNISEX");
    submitData.append("narration", productData.narration || "");

    // ===== WEIGHTS =====
    const grossWeight = parseFloat(productData.grossWeight);
    const netWeight = parseFloat(productData.netWeight);
    submitData.append("grossWeight", isNaN(grossWeight) ? 0 : grossWeight);
    submitData.append("netWeight", isNaN(netWeight) ? 0 : netWeight);

    // ===== METAL DETAILS - ALIGNED: Top-level fields =====
    submitData.append(
      "purity",
      productData.purity || productData.metalDetails?.purity || ""
    );
    submitData.append(
      "metalColor",
      productData.metalColor || productData.metalDetails?.color || "YELLOW"
    );

    // ===== MANUFACTURING - ALIGNED: Individual fields + batchNumber =====
    submitData.append(
      "karigarCode",
      productData.karigarCode || productData.manufacturing?.karigarCode || ""
    );
    if (productData.craftType || productData.manufacturing?.craftType) {
      const craftType =
        productData.craftType || productData.manufacturing?.craftType;
      if (craftType && craftType.trim() !== "" && craftType !== "Select") {
        submitData.append("craftType", craftType);
      }
    }
    if (productData.technique || productData.manufacturing?.technique) {
      const technique =
        productData.technique || productData.manufacturing?.technique;
      if (technique && technique.trim() !== "" && technique !== "Select") {
        submitData.append("technique", technique);
      }
    }
    if (productData.manufacturing?.origin) {
      submitData.append("origin", productData.manufacturing.origin);
    }
    if (productData.manufacturing?.batchNumber) {
      submitData.append("batchNumber", productData.manufacturing.batchNumber);
    }

    // ===== CATEGORIES - ALIGNED: Array notation =====
    if (productData.categories && productData.categories.length > 0) {
      productData.categories.forEach((catId) => {
        if (catId) {
          submitData.append("categories[]", catId);
        }
      });
    }

    // ===== OTHER ARRAYS - ALIGNED: Array notation =====
    (productData.collections || []).forEach((item) => {
      submitData.append("collections[]", item);
    });
    (productData.occasion || []).forEach((item) => {
      submitData.append("occasion[]", item);
    });
    (productData.tags || []).forEach((item) => {
      submitData.append("tags[]", item);
    });
    (productData.viewAccess || ["B2C_WEBSITE", "B2C_APP"]).forEach((item) => {
      submitData.append("viewAccess[]", item);
    });

    // ===== STONES - ALIGNED with ProductForm structure =====
    if (productData.hasStones && productData.stones?.length > 0) {
      submitData.append("hasStones", "true");
      const stonesForSubmit = productData.stones.map((stone) => ({
        type: stone.type || "DIAMOND",
        weight: parseFloat(stone.weight) || 0,
        count: parseInt(stone.count) || 1,
        color: stone.color || "",
        clarity: stone.clarity || "",
        cut: stone.cut || "",
        shape: stone.shape || "ROUND",
        settingType: stone.settingType || "PRONG",
        certification: {
          agency: stone.certification?.agency || "",
          certificateNumber: stone.certification?.certificateNumber || "",
          certificateUrl: stone.certification?.certificateUrl || "",
        },
        ratePerCarat: parseFloat(stone.ratePerCarat) || 0,
        totalValue: parseFloat(stone.totalValue) || 0,
      }));
      submitData.append("stones", JSON.stringify(stonesForSubmit));

      const totalStoneWeight = stonesForSubmit.reduce(
        (sum, stone) => sum + (stone.weight || 0),
        0
      );
      const stoneCharges = stonesForSubmit.reduce(
        (sum, stone) => sum + (stone.totalValue || 0),
        0
      );
      submitData.append("totalStoneWeight", totalStoneWeight);
      submitData.append("stoneCharges", stoneCharges);

      submitData.append("isStone", "true");
      submitData.append("stoneWeight", totalStoneWeight);
    } else {
      submitData.append("hasStones", "false");
      submitData.append("isStone", "false");
    }

    // ===== CERTIFICATIONS - ALIGNED =====
    if (productData.certifications && productData.certifications.length > 0) {
      submitData.append(
        "certifications",
        JSON.stringify(productData.certifications)
      );
    }

    // ===== SIZE INFO - ALIGNED =====
    if (productData.sizeInfo?.hasSize) {
      submitData.append("sizeInfo", JSON.stringify(productData.sizeInfo));
    }

    // ===== VARIANTS - ALIGNED =====
    if (productData.isVariant) {
      submitData.append("isVariant", "true");
      if (productData.parentProduct) {
        submitData.append("parentProduct", productData.parentProduct);
      }
      if (productData.variantAttribute) {
        submitData.append("variantAttribute", productData.variantAttribute);
      }
    }
    if (productData.variants && productData.variants.length > 0) {
      submitData.append("variants", JSON.stringify(productData.variants));
    }

    // ===== CUSTOMIZATION - ALIGNED =====
    if (productData.customizable?.isCustomizable) {
      submitData.append(
        "customizable",
        JSON.stringify(productData.customizable)
      );
    }

    // ===== PRICING CONFIG - ALIGNED =====
    const pricingConfigObject = {
      useCustomPricing: productData.pricingConfig?.useCustomPricing || false,
      customMakingCharges:
        productData.pricingConfig?.customMakingCharges || null,
      customWastagePercent:
        productData.pricingConfig?.customWastagePercent || null,
      excludeFromOffers: productData.pricingConfig?.excludeFromOffers || false,
      branchSpecificRules: [],
    };
    submitData.append("pricingConfig", JSON.stringify(pricingConfigObject));

    // ===== BRANCH INFO - ALIGNED: Individual fields =====
    const primaryBranch =
      productData.branchInfo?.primaryBranch || productData.primaryBranch || "";
    if (primaryBranch) {
      submitData.append("primaryBranch", primaryBranch);
      submitData.append("branchCode", primaryBranch);
    }

    // ===== STOCK - ALIGNED with ProductForm stockData structure =====
    const locationStocks = productData.stock?.locationStock || [];

    if (locationStocks.length > 0) {
      const totalQuantity = locationStocks.reduce(
        (sum, loc) => sum + (parseInt(loc.quantity) || 0),
        0
      );
      const totalReserved = locationStocks.reduce(
        (sum, loc) => sum + (parseInt(loc.reserved) || 0),
        0
      );
      const totalAvailable = locationStocks.reduce(
        (sum, loc) => sum + (parseInt(loc.available) || 0),
        0
      );

      const stockObject = {
        totalQuantity: totalQuantity || 0,
        reservedQuantity: totalReserved || 0,
        availableQuantity: totalAvailable || 0,
        minStockLevel: productData.stock?.minStockLevel || 1,
        maxStockLevel: productData.stock?.maxStockLevel || null,
        locationStock: locationStocks.map((stock) => ({
          branchCode: stock.branchCode,
          storeCode: stock.storeCode || null,
          quantity: Number(stock.quantity) || 0,
          reserved: Number(stock.reserved) || 0,
          available: Number(stock.available) || 0,
          displayQuantity: Number(stock.displayQuantity) || 0,
          _id: stock._id || undefined,
        })),
      };

      submitData.append("stockData", JSON.stringify(stockObject));
      submitData.append("stock", stockObject.totalQuantity);
      submitData.append("minStockLevel", stockObject.minStockLevel);
      if (stockObject.maxStockLevel) {
        submitData.append("maxStockLevel", stockObject.maxStockLevel);
      }
    } else {
      const totalQty = productData.stock?.totalQuantity || 1;
      submitData.append("stock", totalQty);
      submitData.append("minStockLevel", productData.stock?.minStockLevel || 1);
      if (productData.stock?.maxStockLevel) {
        submitData.append("maxStockLevel", productData.stock.maxStockLevel);
      }
    }

    // ===== HALLMARK - ALIGNED: Individual fields like ProductForm =====
    if (productData.hallmark?.isHallmarked) {
      submitData.append("isHallmarked", "true");
      submitData.append("huid", productData.hallmark.huid || "");
      submitData.append(
        "hallmarkCenterCode",
        productData.hallmark.centerCode || ""
      );
      submitData.append(
        "hallmarkYear",
        productData.hallmark.year || new Date().getFullYear().toString()
      );
      submitData.append(
        "hallmarkJewellerCode",
        productData.hallmark.jewellerCode || ""
      );
      submitData.append(
        "hallmarkPurity",
        productData.hallmark.purity || productData.purity || ""
      );
    } else {
      submitData.append("isHallmarked", "false");
    }

    // ===== DISPLAY FLAGS - ALIGNED =====
    submitData.append("isFeatured", productData.isFeatured || false);
    submitData.append("isNewArrival", productData.isNewArrival || false);
    submitData.append("isBestseller", productData.isBestseller || false);
    submitData.append("isExclusive", productData.isExclusive || false);
    submitData.append("isPrivate", productData.isPrivate || false);

    // ===== MEDIA FILES =====
    if (productData.mediaFiles?.images?.length) {
      productData.mediaFiles.images.forEach((file) => {
        submitData.append("images", file);
      });
    }
    if (productData.mediaFiles?.videos?.length) {
      productData.mediaFiles.videos.forEach((file) => {
        submitData.append("videos", file);
      });
    }

    return submitData;
  }, []);

  // Process single item
  const processItem = useCallback(
    async (item) => {
      if (processingRef.current.has(item.id)) return;

      processingRef.current.add(item.id);
      updateItemStatus(item.id, "processing");

      try {
        const formData = buildFormData(item.data);

        // Debug logging in development
        if (process.env.NODE_ENV === "development") {
          console.log("🔹 useProductQueue - Processing item:", item.name);
        }

        const result = await dispatch(createProductThunk(formData)).unwrap();

        // Check if data was edited while processing
        const currentVersion = dataVersionRef.current[item.id];
        if (currentVersion && currentVersion !== item.dataVersion) {
          // Data was edited, mark as needing re-save
          updateItemStatus(
            item.id,
            "edited",
            "Product was edited during save. Please save again."
          );
          toast.warning(`"${item.name}" was edited. Please save again.`);
        } else {
          updateItemStatus(
            item.id,
            "completed",
            null,
            result?._id || result?.id
          );
          toast.success(`"${item.name}" saved successfully`);
        }
      } catch (error) {
        console.error("🔸 useProductQueue - Error:", error);
        const retryCount = retryCountRef.current[item.id] || 0;

        // Extract meaningful error message
        let errorMessage = "Failed to save";
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }

        if (retryCount < MAX_RETRIES) {
          retryCountRef.current[item.id] = retryCount + 1;
          updateItemStatus(
            item.id,
            "pending",
            `Retry ${retryCount + 1}/${MAX_RETRIES}: ${errorMessage}`
          );

          setTimeout(() => {
            processingRef.current.delete(item.id);
          }, RETRY_DELAY);
        } else {
          updateItemStatus(item.id, "failed", errorMessage);
          toast.error(`Failed to save "${item.name}": ${errorMessage}`);
        }
      } finally {
        processingRef.current.delete(item.id);
      }
    },
    [dispatch, buildFormData, updateItemStatus]
  );

  // Process queue
  useEffect(() => {
    if (isPaused) return;

    const pendingItems = queue.filter(
      (item) => item.status === "pending" && !processingRef.current.has(item.id)
    );

    const availableSlots = MAX_CONCURRENT - processingRef.current.size;
    const itemsToProcess = pendingItems.slice(0, availableSlots);

    itemsToProcess.forEach((item) => {
      processItem(item);
    });
  }, [queue, isPaused, processItem]);

  // Retry failed item
  const retryItem = useCallback(
    (itemId) => {
      retryCountRef.current[itemId] = 0;
      updateItemStatus(itemId, "pending", null);
    },
    [updateItemStatus]
  );

  // Retry failed item with new data
  const retryWithData = useCallback(
    (itemId, newData, newName) => {
      retryCountRef.current[itemId] = 0;
      updateQueueItemData(itemId, newData, newName);
      updateItemStatus(itemId, "pending", null);
    },
    [updateQueueItemData, updateItemStatus]
  );

  // Pause/Resume
  const pauseQueue = useCallback(() => setIsPaused(true), []);
  const resumeQueue = useCallback(() => setIsPaused(false), []);

  // Clear completed
  const clearCompleted = useCallback(() => {
    const completedIds = queue
      .filter((item) => item.status === "completed")
      .map((item) => item.id);

    setQueue((prev) => prev.filter((item) => item.status !== "completed"));

    // Cleanup refs
    completedIds.forEach((id) => {
      delete retryCountRef.current[id];
      delete dataVersionRef.current[id];
    });
  }, [queue]);

  // Clear all (with confirmation requirement)
  const clearAll = useCallback(() => {
    // Only clear non-processing items
    const idsToRemove = queue
      .filter((item) => item.status !== "processing")
      .map((item) => item.id);

    setQueue((prev) => prev.filter((item) => item.status === "processing"));

    idsToRemove.forEach((id) => {
      delete retryCountRef.current[id];
      delete dataVersionRef.current[id];
    });
  }, [queue]);

  // Stats
  const stats = useMemo(
    () => ({
      total: queue.length,
      pending: queue.filter((i) => i.status === "pending").length,
      saving: queue.filter((i) => i.status === "processing").length,
      completed: queue.filter((i) => i.status === "completed").length,
      failed: queue.filter((i) => i.status === "failed").length,
      edited: queue.filter((i) => i.status === "edited").length,
    }),
    [queue]
  );

  const isProcessing = processingRef.current.size > 0;

  return {
    queue,
    addToQueue,
    updateQueueItemData,
    removeFromQueue,
    retryItem,
    retryWithData,
    pauseQueue,
    resumeQueue,
    clearCompleted,
    clearAll,
    isProcessing,
    isPaused,
    stats,
    isInQueue,
    getQueueItem,
  };
};
